export type MarkType = 'ship' | 'noship' | 'partial';

export const MARK_TYPE_LABELS: Record<MarkType, string> = {
  ship: '정상 출고',
  noship: '출고 없음',
  partial: '부분 출고',
};

export const MARK_TYPE_DEFAULT_LABEL: Record<MarkType, string> = {
  ship: '정상 출고',
  noship: '출고 없음',
  partial: '부분 출고',
};

// 구버전 MarkType → 신버전 매핑 (localStorage 하위호환)
const LEGACY_MARK_TYPE: Record<string, MarkType> = {
  cutoff: 'ship',
  resume: 'ship',
  other: 'noship',
  holiday: 'noship',
};

// 한국 공휴일 맵 (YYYY-MM-DD → 이름). 달력 렌더링 시 자동으로 빨간 숫자 + 라벨 적용.
// 음력 기반 공휴일은 해마다 다르므로 필요한 연도 미리 정의.
export const KOREAN_HOLIDAYS: Record<string, string> = {
  // 2025
  '2025-01-01': '신정',
  '2025-01-28': '설날연휴',
  '2025-01-29': '설날',
  '2025-01-30': '설날연휴',
  '2025-03-01': '삼일절',
  '2025-03-03': '대체공휴일',
  '2025-05-01': '근로자의날',
  '2025-05-05': '어린이날',
  '2025-05-06': '대체공휴일',
  '2025-06-06': '현충일',
  '2025-08-15': '광복절',
  '2025-10-03': '개천절',
  '2025-10-06': '추석연휴',
  '2025-10-07': '추석',
  '2025-10-08': '추석연휴',
  '2025-10-09': '한글날',
  '2025-12-25': '성탄절',
  // 2026
  '2026-01-01': '신정',
  '2026-02-16': '설날연휴',
  '2026-02-17': '설날',
  '2026-02-18': '설날연휴',
  '2026-03-01': '삼일절',
  '2026-05-01': '근로자의날',
  '2026-05-05': '어린이날',
  '2026-05-24': '부처님오신날',
  '2026-05-25': '대체공휴일',
  '2026-06-03': '지방선거',
  '2026-06-06': '현충일',
  '2026-08-15': '광복절',
  '2026-08-17': '대체공휴일',
  '2026-09-24': '추석연휴',
  '2026-09-25': '추석',
  '2026-09-26': '추석연휴',
  '2026-10-03': '개천절',
  '2026-10-05': '대체공휴일',
  '2026-10-09': '한글날',
  '2026-12-25': '성탄절',
  // 2027
  '2027-01-01': '신정',
  '2027-02-06': '설날연휴',
  '2027-02-07': '설날',
  '2027-02-08': '설날연휴',
  '2027-03-01': '삼일절',
  '2027-05-01': '근로자의날',
  '2027-05-05': '어린이날',
  '2027-05-13': '부처님오신날',
  '2027-06-06': '현충일',
  '2027-08-15': '광복절',
  '2027-09-14': '추석연휴',
  '2027-09-15': '추석',
  '2027-09-16': '추석연휴',
  '2027-10-03': '개천절',
  '2027-10-09': '한글날',
  '2027-12-25': '성탄절',
};

export function getHolidayName(isoDate: string): string | null {
  return KOREAN_HOLIDAYS[isoDate] ?? null;
}

export interface DayMark {
  date: string; // ISO YYYY-MM-DD
  type: MarkType;
  label: string;
}

export interface ExtraLabel {
  date: string; // ISO YYYY-MM-DD
  label: string;
}

export interface ShippingCalendarConfig {
  version: 1;
  title: string;
  subtitle: string;
  contactPhone: string;
  footerMessage: string;
  startDate: string; // ISO; 주의 일요일
  endDate: string;   // ISO; 주의 토요일
  marks: DayMark[];
  extraLabels: ExtraLabel[]; // 자동 공휴일 외에 추가로 표시할 라벨 (지방선거 등)
}

export const DEFAULT_FOOTER_MESSAGE = '· 택배 관련 문의는 고객센터(010-5850-4634)로 연락주세요.';

export interface PresetEntry {
  id: string;
  name: string;
  config: ShippingCalendarConfig;
  savedAt: string;
}

const LAST_KEY = 'shipping-calendar:last';
const PRESETS_KEY = 'shipping-calendar:presets';

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fromIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function snapToWeekStart(isoDate: string): string {
  const d = fromIsoDate(isoDate);
  d.setDate(d.getDate() - d.getDay()); // 일요일까지 뒤로
  return toIsoDate(d);
}

export function snapToWeekEnd(isoDate: string): string {
  const d = fromIsoDate(isoDate);
  d.setDate(d.getDate() + (6 - d.getDay())); // 토요일까지 앞으로
  return toIsoDate(d);
}

export function weeksBetween(startIso: string, endIso: string): number {
  const start = fromIsoDate(startIso);
  const end = fromIsoDate(endIso);
  const days = (end.getTime() - start.getTime()) / (24 * 3600 * 1000) + 1;
  return Math.ceil(days / 7);
}

export function getWeekRows(startIso: string, endIso: string): string[][] {
  const rows: string[][] = [];
  const cursor = fromIsoDate(startIso);
  const end = fromIsoDate(endIso);
  while (cursor.getTime() <= end.getTime()) {
    const row: string[] = [];
    for (let i = 0; i < 7; i += 1) {
      row.push(toIsoDate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    rows.push(row);
  }
  return rows;
}

export function upsertMark(marks: DayMark[], mark: DayMark): DayMark[] {
  const next = marks.filter(m => m.date !== mark.date);
  next.push(mark);
  return next.sort((a, b) => a.date.localeCompare(b.date));
}

export function removeMark(marks: DayMark[], date: string): DayMark[] {
  return marks.filter(m => m.date !== date);
}

export function findMark(marks: DayMark[], date: string): DayMark | undefined {
  return marks.find(m => m.date === date);
}

export function upsertExtraLabel(labels: ExtraLabel[], entry: ExtraLabel): ExtraLabel[] {
  const next = labels.filter(l => l.date !== entry.date);
  next.push(entry);
  return next.sort((a, b) => a.date.localeCompare(b.date));
}

export function removeExtraLabel(labels: ExtraLabel[], date: string): ExtraLabel[] {
  return labels.filter(l => l.date !== date);
}

export function findExtraLabel(labels: ExtraLabel[], date: string): ExtraLabel | undefined {
  return labels.find(l => l.date === date);
}

export function snapConfig(config: ShippingCalendarConfig): ShippingCalendarConfig {
  return {
    ...config,
    startDate: snapToWeekStart(config.startDate),
    endDate: snapToWeekEnd(config.endDate),
  };
}

export function createEmptyConfig(): ShippingCalendarConfig {
  const today = new Date();
  const start = snapToWeekStart(toIsoDate(today));
  const endDate = fromIsoDate(start);
  endDate.setDate(endDate.getDate() + 13); // 2주
  return {
    version: 1,
    title: '3월 배송 일정 안내',
    subtitle: '공휴일 배송 일정을 알려드려요.\n주문 시 참고해 주세요.',
    contactPhone: '010-5850-4634',
    footerMessage: DEFAULT_FOOTER_MESSAGE,
    startDate: start,
    endDate: snapToWeekEnd(toIsoDate(endDate)),
    marks: [],
    extraLabels: [],
  };
}

export function loadConfig(): ShippingCalendarConfig | null {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.version === 1 && Array.isArray(parsed.marks)) {
      // 구버전 MarkType을 신버전으로 매핑
      const migratedMarks = parsed.marks.map((m: DayMark) => {
        const migrated = LEGACY_MARK_TYPE[m.type];
        return migrated ? { ...m, type: migrated } : m;
      });
      // footerMessage 누락 시 contactPhone으로 기본 메시지 생성
      const footerMessage = typeof parsed.footerMessage === 'string'
        ? parsed.footerMessage
        : `· 택배 관련 문의는 고객센터(${parsed.contactPhone || '010-5850-4634'})로 연락주세요.`;
      const extraLabels = Array.isArray(parsed.extraLabels) ? parsed.extraLabels : [];
      return { ...parsed, marks: migratedMarks, footerMessage, extraLabels } as ShippingCalendarConfig;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveConfig(config: ShippingCalendarConfig): void {
  localStorage.setItem(LAST_KEY, JSON.stringify(config));
}

export function clearConfig(): void {
  localStorage.removeItem(LAST_KEY);
}

export function loadPresets(): PresetEntry[] {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PresetEntry[]) : [];
  } catch {
    return [];
  }
}

export function savePresets(presets: PresetEntry[]): void {
  localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
}

export function newPresetId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `p_${crypto.randomUUID()}`;
  }
  return `p_${Math.random().toString(36).slice(2, 10)}`;
}
